import axios from 'axios';
import type {
  MatterRecipientData,
  MatterRecipientsResponse,
  CoCounselExpensesResponse,
  CreateMatterRecipientRequest,
  UpdateMatterRecipientRequest,
} from '../../models/settlementTypes';

const buildUrl = (matterId: string): string =>
  `/matters/${matterId}/settlement/recipients`;

/** GET /matters/{matterId}/settlement/recipients — matter-level fee recipient
 *  defaults, used to auto-populate Step 3 before it has been confirmed. */
export const getMatterRecipients = async (
  matterId: string,
): Promise<MatterRecipientsResponse> => {
  const { data } = await axios.get<MatterRecipientsResponse>(buildUrl(matterId));
  return data;
};

/** Creates one recipient; no bulk endpoint, so callers append the response row locally. */
export const createMatterRecipient = async (
  matterId: string,
  payload: CreateMatterRecipientRequest,
): Promise<MatterRecipientData> => {
  const { data } = await axios.post<MatterRecipientData>(buildUrl(matterId), payload);
  return data;
};

/** Updates one recipient's vendor/role/percentage/sortOrder. Omit `confirmReset` first — a `409`
 *  means the vendor or role changed on a recipient with an already-confirmed Step 2 expense;
 *  retry with `confirmReset: true` after confirming with the user (mirrors delete's VR-013 tiers). */
export const updateMatterRecipient = async (
  matterId: string,
  recipientId: string,
  payload: UpdateMatterRecipientRequest,
  confirmReset?: boolean,
): Promise<MatterRecipientData> => {
  const { data } = await axios.patch<MatterRecipientData>(
    `${buildUrl(matterId)}/${recipientId}`,
    payload,
    { params: confirmReset ? { confirmReset: true } : undefined },
  );
  return data;
};

/** Tiered per VR-013 — omit `confirmReset` first; a `409` means retry with `confirmReset: true`
 *  after confirming with the user; a `400` means locked (Remove is hidden in that state anyway). */
export const deleteMatterRecipient = async (
  matterId: string,
  recipientId: string,
  confirmReset?: boolean,
): Promise<void> => {
  await axios.delete(`${buildUrl(matterId)}/${recipientId}`, {
    params: confirmReset ? { confirmReset: true } : undefined,
  });
};

/** GET /settlements/{settlementId}/expenses/co-counsel — Step 2's co-counsel/
 *  referring-attorney expense rows, joined with any confirmed amount. */
export const getCoCounselExpenses = async (
  settlementId: string,
): Promise<CoCounselExpensesResponse> => {
  const { data } = await axios.get<CoCounselExpensesResponse>(
    `/settlements/${settlementId}/expenses/co-counsel`,
  );
  return data;
};
